import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Truck, Package, Clock, CheckCircle, ArrowRight } from 'lucide-react';

const Services = () => {
  const services = [
    {
      id: 'commercial-freight',
      icon: Truck,
      title: 'Commercial & Freight Transport',
      description: 'Professional freight solutions for businesses of all sizes. Our 26ft box truck with liftgate handles a wide range of commercial cargo with care and efficiency.',
      color: 'red',
      image: 'https://customer-assets-0z36b82j.emergentagent.net/job_box-truck-delivery/artifacts/o93f3n9z_image.png',
      features: [
        { text: 'Palletized Freight', color: 'red' },
        { text: 'Retail & Warehouse Inventory', color: 'blue' },
        { text: 'Office Equipment & Materials', color: 'red' },
        { text: 'Event & Construction Loads', color: 'blue' },
        { text: 'Business & Contractor Services', color: 'red' },
      ],
      bestFor: ['Recurring business deliveries', 'Retail inventory restocks', 'Construction site materials', 'Trade show & event logistics'],
      idealCustomer: 'Businesses, contractors, and retailers needing reliable freight transport across the Southeast.',
      whatSolves: 'Eliminates the hassle of renting trucks, coordinating logistics, and worrying about on-time delivery for your business operations.',
      pricing: 'Starting at $150 for local deliveries',
    },
    {
      id: 'residential-moving',
      icon: Package,
      title: 'Residential & Moving Services',
      description: 'Complete moving solutions for individuals and families. From single furniture pieces to full home relocations, we handle your belongings with care.',
      color: 'blue',
      image: 'https://customer-assets-0z36b82j.emergentagent.net/job_box-truck-delivery/artifacts/iz2jnle1_image.png',
      features: [
        { text: 'Furniture & Appliances', color: 'blue' },
        { text: 'Mattresses & Boxes', color: 'red' },
        { text: 'Storage Unit Moves', color: 'blue' },
        { text: 'Full or Partial Home Moves', color: 'red' },
        { text: 'Trash Removal & Unwanted Clutter', color: 'blue' },
      ],
      bestFor: ['Full home relocations', 'Single-item furniture delivery', 'Military PCS moves', 'Storage unit transfers'],
      idealCustomer: 'Families, military personnel, and individuals moving locally or across the Southeast region.',
      whatSolves: 'Takes the stress out of moving day with professional handling, on-time pickup, and careful delivery of your belongings.',
      pricing: 'Starting at $199 for local moves',
    },
    {
      id: 'on-demand-delivery',
      icon: Clock,
      title: 'On-Demand & Flexible Delivery',
      description: 'Flexible scheduling options for urgent and time-sensitive deliveries. Same-day service available with reliable, efficient execution.',
      color: 'red',
      image: 'https://images.pexels.com/photos/29786116/pexels-photo-29786116.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      features: [
        { text: 'Same-Day & Scheduled Pickup', color: 'red' },
        { text: 'Residential & Commercial', color: 'blue' },
        { text: 'One-Time or Recurring Service', color: 'red' },
        { text: 'Reliable & Efficient Delivery', color: 'blue' },
        { text: 'Flexible Scheduling Options', color: 'red' },
      ],
      bestFor: ['Last-minute delivery needs', 'Time-sensitive shipments', 'Recurring scheduled routes', 'Overflow capacity for businesses'],
      idealCustomer: 'Anyone who needs fast, reliable delivery without long-term commitments or advance planning.',
      whatSolves: 'Gives you a professional delivery partner on-call for urgent and flexible logistics needs — no contracts required.',
      pricing: 'Starting at $99 for same-day delivery',
    },
  ];

  return (
    <div className="bg-texture min-h-screen" data-testid="services-page">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-brand-surface border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
              OUR <span className="text-brand-red">SERVICES</span>
            </h1>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Comprehensive transportation solutions tailored to meet your commercial and residential needs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Detail Sections */}
      {services.map((service, index) => (
        <section 
          key={service.id}
          id={service.id}
          className={`py-24 md:py-32 ${index % 2 === 1 ? 'bg-brand-surface' : ''}`}
          data-testid={`service-section-${service.id}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              {/* Content */}
              <motion.div
                className={index % 2 === 1 ? 'lg:order-2' : ''}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <div className={`icon-container ${service.color} mb-6`}>
                  <service.icon className="w-7 h-7" />
                </div>
                <h2 className="font-heading text-3xl sm:text-4xl text-white mb-4">
                  {service.title.split(' ').map((word, i) => (
                    <span key={`${service.id}-word-${word}-${i}`}>
                      {i === 0 ? (
                        <span className={`text-brand-${service.color}`}>{word} </span>
                      ) : (
                        `${word} `
                      )}
                    </span>
                  ))}
                </h2>
                <p className="text-slate-400 text-lg mb-8">
                  {service.description}
                </p>

                <ul className="space-y-3 mb-6">
                  {service.features.map((feature) => (
                    <li key={feature.text} className="flex items-center gap-3">
                      <CheckCircle className={`w-5 h-5 text-brand-${feature.color} flex-shrink-0`} />
                      <span className="text-slate-300">{feature.text}</span>
                    </li>
                  ))}
                </ul>

                {/* Best For */}
                <div className="mb-6 p-5 rounded-xl bg-white/[0.02] border border-white/5">
                  <p className="text-xs text-brand-red font-heading tracking-wider mb-3">BEST FOR</p>
                  <div className="grid grid-cols-2 gap-2">
                    {service.bestFor.map((item) => (
                      <span key={item} className="text-slate-400 text-sm flex items-center gap-1.5">
                        <span className="w-1 h-1 rounded-full bg-brand-red flex-shrink-0" />{item}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Ideal Customer */}
                <div className="mb-6">
                  <p className="text-xs text-brand-blue font-heading tracking-wider mb-1.5">IDEAL CUSTOMER</p>
                  <p className="text-slate-400 text-sm">{service.idealCustomer}</p>
                </div>

                {/* What This Solves */}
                <div className="mb-8">
                  <p className="text-xs text-slate-500 font-heading tracking-wider mb-1.5">WHAT THIS SOLVES</p>
                  <p className="text-slate-400 text-sm">{service.whatSolves}</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                  <p className="text-slate-500 text-sm italic">{service.pricing}</p>
                  <Link 
                    to="/contact" 
                    className={`inline-flex items-center gap-2 font-semibold text-brand-${service.color} hover:underline`}
                    data-testid={`${service.id}-quote-link`}
                  >
                    Get My Quote
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>

              {/* Image */}
              <motion.div
                className={index % 2 === 1 ? 'lg:order-1' : ''}
                initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <div className="relative rounded-xl overflow-hidden">
                  <img 
                    src={service.image}
                    alt={service.title}
                    className="w-full object-cover aspect-[4/3]"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-tr from-brand-${service.color}/30 to-transparent`} />
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* Service Guarantee Callout */}
      <section className="py-16 border-b border-white/5" data-testid="services-guarantee-callout">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 py-6 px-8 rounded-2xl bg-brand-navy border border-white/10"
          >
            <div className="w-12 h-12 rounded-xl bg-brand-red/10 flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-6 h-6 text-brand-red" />
            </div>
            <div className="text-center sm:text-left">
              <p className="text-white font-heading text-lg">Backed by the <span className="text-brand-red">TopFleet Service Guarantee</span></p>
              <p className="text-slate-400 text-sm">On-time delivery, damage protection, and professional service — or we make it right.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Equipment Section */}
      <section className="py-24 md:py-32 bg-brand-surface" data-testid="equipment-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-4">
              OUR <span className="text-brand-blue">EQUIPMENT</span>
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Professional-grade equipment for safe, efficient transport
            </p>
          </motion.div>

          <motion.div
            className="bg-brand-navy rounded-xl border border-white/10 p-8 md:p-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="font-heading text-2xl text-white mb-4">
                  26FT BOX TRUCK <span className="text-brand-red">WITH LIFTGATE</span>
                </h3>
                <p className="text-slate-400 mb-6">
                  Our primary vehicle is a professional-grade 26ft box truck equipped with a hydraulic liftgate for easy loading and unloading of heavy items.
                </p>
                <ul className="grid grid-cols-2 gap-4">
                  <li className="flex items-center gap-2 text-slate-300">
                    <CheckCircle className="w-4 h-4 text-brand-red" />
                    Hydraulic Liftgate
                  </li>
                  <li className="flex items-center gap-2 text-slate-300">
                    <CheckCircle className="w-4 h-4 text-brand-blue" />
                    1,600+ Cubic Feet
                  </li>
                  <li className="flex items-center gap-2 text-slate-300">
                    <CheckCircle className="w-4 h-4 text-brand-red" />
                    10,000 lb Capacity
                  </li>
                  <li className="flex items-center gap-2 text-slate-300">
                    <CheckCircle className="w-4 h-4 text-brand-blue" />
                    Secure Tie-Downs
                  </li>
                </ul>
              </div>
              <div className="relative">
                <div className="rounded-xl overflow-hidden border border-white/10 shadow-xl">
                  <img
                    src="https://customer-assets-0z36b82j.emergentagent.net/job_box-truck-delivery/artifacts/zgz6q6i6_image.png"
                    alt="TopFleet Logistics 26ft box truck at sunset"
                    className="w-full h-auto object-cover"
                    loading="lazy"
                    data-testid="equipment-truck-photo"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
              NEED A <span className="text-brand-red">CUSTOM SOLUTION?</span>
            </h2>
            <p className="text-xl text-slate-400 mb-3">
              Contact us to discuss your specific transportation needs. We'll create a tailored solution for you.
            </p>
            <p className="text-brand-red/80 text-sm font-medium mb-10">We respond within 1 hour &middot; Real-time updates via call & text</p>
            <Link 
              to="/contact" 
              className="btn-cta text-lg px-10 py-4 inline-block"
              data-testid="services-cta-button"
            >
              Get My Quote
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
